import React from "react";
import {
  Card as UICard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import { BarChart } from "@/components/Base/BarChart";
import { ScatterChart } from "@/components/Base/ScatterChart";
import { Skeleton } from "@/components/Base/Skeleton";
// Using custom alert styling instead of Alert component
import { RefreshCwIcon, AlertTriangleIcon, TrendingUpIcon } from "lucide-react";
import { Button } from "@/components/Base/Button";
import {
  ContainerTimeMetric,
  BarChartDataPoint,
  ScatterPlotDataPoint,
  SerializedContainerTimeMetric,
} from "@/types/v2/PrecomputedMetrics";
import { firestoreToDateTime } from "@/lib/utils";
import { format } from "date-fns";

interface MetricsChartDisplayProps {
  metric:
    | ContainerTimeMetric
    | SerializedContainerTimeMetric
    | null
    | undefined;
  chartType: "bar" | "scatter";
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isStale: boolean | undefined;
  backgroundRefresh: boolean | undefined;
  cached: boolean | undefined;
  computedAt?: string;
  onRefresh: () => void;
}

export const MetricsChartDisplay: React.FC<MetricsChartDisplayProps> = ({
  metric,
  chartType,
  isLoading,
  isError,
  error,
  isStale = false,
  backgroundRefresh = false,
  cached = false,
  computedAt,
  onRefresh,
}) => {
  if (isLoading) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUpIcon className="h-5 w-5" />
            Metrics Chart
            <div className="ml-auto">
              <Skeleton className="h-6 w-20" />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-[400px] w-full" />
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          </div>
        </CardContent>
      </UICard>
    );
  }

  if (isError) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangleIcon className="h-5 w-5 text-destructive" />
            Metrics Chart - Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2">
              <AlertTriangleIcon className="h-4 w-4 text-red-600" />
              <span className="text-red-800">
                Failed to load metrics data: {error?.message || "Unknown error"}
              </span>
            </div>
          </div>
          <Button onClick={onRefresh} className="mt-4" variant="outline">
            <RefreshCwIcon className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </CardContent>
      </UICard>
    );
  }

  if (!metric) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUpIcon className="h-5 w-5" />
            Metrics Chart
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-12 text-center text-muted-foreground">
            No data available for the selected configuration and date range.
          </div>
        </CardContent>
      </UICard>
    );
  }

  const barChartData: BarChartDataPoint[] =
    chartType === "bar" ? metric.chartData.barChart : [];
  const scatterChartData: ScatterPlotDataPoint[] =
    chartType === "scatter" ? metric.chartData.scatterPlot : [];

  return (
    <UICard>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUpIcon className="h-5 w-5" />
          Metrics Chart
          <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            {isStale && (
              <div className="flex items-center gap-1 text-amber-600">
                <AlertTriangleIcon className="h-4 w-4" />
                Stale Data
                {backgroundRefresh && (
                  <RefreshCwIcon className="h-4 w-4 animate-spin" />
                )}
              </div>
            )}
            {cached && <span className="text-green-600">Cached</span>}
            {computedAt && <span>{format(new Date(computedAt), "Pp")}</span>}
            <Button
              onClick={onRefresh}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
            >
              <RefreshCwIcon className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Stale Data Warning */}
          {isStale && !backgroundRefresh && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2">
                <AlertTriangleIcon className="h-4 w-4 text-amber-600" />
                <span className="text-amber-800">
                  This data may be outdated. Click refresh to get the latest
                  metrics.
                </span>
              </div>
            </div>
          )}

          {/* Chart */}
          <div className="h-[400px]">
            {chartType === "bar" ? (
              <BarChart
                data={barChartData.map((point) => ({
                  range: point.range,
                  count: point.count,
                }))}
                index="range"
                categories={["count"]}
              />
            ) : (
              <ScatterChart
                data={scatterChartData}
                xKey="firstEventTime"
                yKey="timeDifference"
                height={400}
                isTimeAxis={true}
              />
            )}
          </div>

          {/* Statistics Summary */}
          {metric.statistics && (
            <div className="grid grid-cols-2 gap-4 border-t pt-4 md:grid-cols-4">
              <div className="text-center">
                <div className="text-2xl font-semibold">
                  {metric.statistics.averageTime.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground">Average</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold">
                  {metric.statistics.medianTime.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground">Median</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold">
                  {metric.statistics.minTime.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground">Min</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold">
                  {metric.statistics.maxTime.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground">Max</div>
              </div>
            </div>
          )}

          {/* Cache Information */}
          <div className="border-t pt-2 text-center text-xs text-muted-foreground">
            {(() => {
              const computedAt = firestoreToDateTime(metric.cache.computedAt);
              const lastAccessedAt = firestoreToDateTime(
                metric.cache.lastAccessedAt
              );

              return (
                <>
                  {computedAt && computedAt.toLocaleString && (
                    <span>Last computed: {computedAt.toLocaleString()}</span>
                  )}
                  {lastAccessedAt && lastAccessedAt.toLocaleString && (
                    <span className="ml-4">
                      Last accessed: {lastAccessedAt.toLocaleString()}
                    </span>
                  )}
                  {!computedAt && !lastAccessedAt && (
                    <span>Cache information unavailable</span>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </CardContent>
    </UICard>
  );
};
